import { AlertCircle, ArrowDownUp, Info, Loader2, RefreshCw } from "lucide-react";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaAmountInputControlled } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { QuoteBreakdown } from "@/shared/components/payments/QuoteBreakdown";
import { QuoteCountdownTimer } from "@/shared/components/payments/QuoteCountdownTimer";
import { RailMaintenanceNotice } from "@/shared/components/payments/RailMaintenanceNotice";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { compareDecimalStrings, isDecimalString } from "@/shared/utils/decimal";

import { useSwaps } from "../hooks/useSwaps";
import type { SwapCurrencyOption } from "../types/swaps-api.types";

/** Wait for the user to stop typing before asking for a price. */
const QUOTE_DEBOUNCE_DELAY = 500;

interface SwapFormProps {
  onConfirm: () => Promise<void>;
}

/**
 * Exact "does the debit exceed the balance", on the shared decimal helpers rather than a second local
 * implementation — two arithmetics for the same money in one runtime is how they drift apart.
 *
 * The guard is not ceremony: `compareDecimalStrings` scales through `BigInt`, which THROWS on anything that
 * is not plain digits. The old string comparison here silently answered `false` instead, and these operands
 * come straight off the wire without validation — so swapping the call without the guard would turn a wrong
 * boolean into a blank screen. Malformed input skips the check; the backend still refuses the swap.
 */
function exceedsBalance(debit: string, available: string): boolean {
  const a = debit.trim();
  const b = available.trim();
  if (!isDecimalString(a) || !isDecimalString(b)) return false;
  return compareDecimalStrings(a, b) > 0;
}

interface FieldProps {
  id: string;
  label: string;
  error?: string;
  /** Spinner next to the label while the OTHER amount field is driving the quote. */
  busy?: boolean;
  /** Balance line or activation note rendered under the control. */
  hint?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Label + control + error, composed by hand: the `Coloca*Field` components read from a React Hook Form
 * context and this form keeps its state in the swaps slice instead.
 */
const Field: React.FC<FieldProps> = ({ id, label, error, busy, hint, children }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-2">
      <ColocaLabel htmlFor={id}>{label}</ColocaLabel>
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />}
    </div>
    {children}
    {error ? (
      <p className="text-xs text-destructive" role="alert">
        {error}
      </p>
    ) : (
      hint
    )}
  </div>
);

export const SwapForm: React.FC<SwapFormProps> = ({ onConfirm }) => {
  const { t } = useTranslations("swaps");
  const fieldId = useId();
  const {
    originOptions,
    destinationOptions,
    walletFor,
    walletsLoading,
    quoteData,
    quoteLoading,
    quoteError,
    quoteExpired,
    fetchQuote,
    markQuoteExpired,
    formData,
    updateField,
    isSubmitting,
    submitError,
  } = useSwaps();

  const [lastChangedField, setLastChangedField] = useState<"origin" | "destination" | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const quoteDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (quoteDebounceRef.current) clearTimeout(quoteDebounceRef.current);
    },
    [],
  );

  const originWallet = walletFor(formData.origin_currency);
  const destinationWallet = walletFor(formData.destination_currency);
  const corridors = useAvailableCorridors();
  const destinationInMaintenance =
    !!formData.destination_currency && corridors.currencyInMaintenance(formData.destination_currency);

  const renderCurrencyLabel = (option: SwapCurrencyOption) => (
    <span className="flex items-center gap-2">
      <span>{option.currency}</span>
      {!option.wallet && (
        <span className="text-xs text-muted-foreground">{t("wallet_not_activated_hint")}</span>
      )}
    </span>
  );

  const toSelectOptions = (options: SwapCurrencyOption[]) =>
    options.map((option) => ({ value: option.currency, label: renderCurrencyLabel(option) }));

  const originSelectOptions = toSelectOptions(originOptions);
  // A swap needs two different currencies — the backend rejects an identical pair at quote time.
  const destinationSelectOptions = toSelectOptions(
    destinationOptions.filter((o) => o.currency !== formData.origin_currency),
  );

  const requestQuote = useCallback(
    (field: "amount_origin" | "amount_destination", value: string) => {
      if (quoteDebounceRef.current) {
        clearTimeout(quoteDebounceRef.current);
        quoteDebounceRef.current = null;
      }
      if (!formData.origin_currency || !formData.destination_currency || !value) return;
      if (!/^\d+(\.\d+)?$/.test(value) || Number(value) <= 0) return;

      quoteDebounceRef.current = setTimeout(() => {
        void fetchQuote({
          originCurrency: formData.origin_currency,
          destinationCurrency: formData.destination_currency,
          ...(field === "amount_origin" ? { sendAmount: value } : { receiveAmount: value }),
        });
      }, QUOTE_DEBOUNCE_DELAY);
    },
    [formData.origin_currency, formData.destination_currency, fetchQuote],
  );

  const handleAmountChange = useCallback(
    (field: "amount_origin" | "amount_destination", value: string) => {
      updateField(field, value);
      setLastChangedField(field === "amount_origin" ? "origin" : "destination");
      setFormErrors({});
      requestQuote(field, value);
    },
    [updateField, requestQuote],
  );

  const handleCurrencyChange = useCallback(
    (field: "origin_currency" | "destination_currency", value: string) => {
      updateField(field, value);
      // The pair changed, so the frozen price no longer applies; clear both amounts and re-quote on input.
      updateField("amount_origin", "");
      updateField("amount_destination", "");
      setFormErrors({});
      const opposite = field === "origin_currency" ? "destination_currency" : "origin_currency";
      if (value && formData[opposite] === value) updateField(opposite, "");
    },
    [updateField, formData],
  );

  const handleRecalculate = useCallback(() => {
    const field = lastChangedField === "destination" ? "amount_destination" : "amount_origin";
    const value = formData[field];
    if (!value) return;
    void fetchQuote({
      originCurrency: formData.origin_currency,
      destinationCurrency: formData.destination_currency,
      ...(field === "amount_origin" ? { sendAmount: value } : { receiveAmount: value }),
    });
  }, [lastChangedField, formData, fetchQuote]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.origin_currency) errors.origin_currency = t("error_origin_required");
    if (!formData.destination_currency) errors.destination_currency = t("error_destination_required");
    if (!formData.amount_origin) errors.amount_origin = t("error_amount_required");
    if (!quoteData) errors.amount_origin = errors.amount_origin ?? t("error_no_quote");

    // Balances never go negative: the origin wallet must fully cover the gross debit, fee included.
    if (quoteData && originWallet && exceedsBalance(quoteData.amounts.send, originWallet.balance_available)) {
      const field = lastChangedField === "destination" ? "amount_destination" : "amount_origin";
      errors[field] = t("error_insufficient_balance");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    await onConfirm();
  };

  const isFormValid =
    !!formData.origin_currency &&
    !!formData.destination_currency &&
    !!quoteData &&
    !quoteLoading &&
    !quoteError &&
    !quoteExpired;

  if (walletsLoading && originOptions.length === 0) {
    return (
      <ColocaSurface variant="panel" padding="lg" className="space-y-6">
        {[0, 1].map((group) => (
          <div key={group} className="space-y-1.5">
            <ColocaSkeleton className="h-3.5 w-28" />
            <ColocaSkeleton className="h-9 w-full" />
            <ColocaSkeleton className="h-3 w-40" />
          </div>
        ))}
        <ColocaSkeleton className="h-9 w-full" />
      </ColocaSurface>
    );
  }

  if (originOptions.length === 0) {
    return <ColocaEmpty icon={Info} title={t("no_wallets_title")} description={t("no_wallets_message")} />;
  }

  return (
    <ColocaSurface variant="panel" padding="lg" className="space-y-6">
      <Field
        id={`${fieldId}-origin`}
        label={t("origin_label")}
        error={formErrors.origin_currency}
        hint={
          originWallet && (
            <p className="text-xs text-muted-foreground">
              {t("available_balance_label")}{" "}
              <ColocaAmountDisplay
                value={originWallet.balance_available}
                currency={originWallet.currency}
                size="sm"
                minimumFractionDigits={2}
                maximumFractionDigits={8}
              />
            </p>
          )
        }
      >
        <ColocaSelect
          id={`${fieldId}-origin`}
          value={formData.origin_currency || ""}
          onValueChange={(value) => handleCurrencyChange("origin_currency", value)}
          options={originSelectOptions}
          placeholder={t("select_origin")}
        />
      </Field>

      <Field
        id={`${fieldId}-amount-origin`}
        label={t("amount_origin_label")}
        error={formErrors.amount_origin}
        busy={quoteLoading && lastChangedField === "destination"}
      >
        <ColocaAmountInputControlled
          id={`${fieldId}-amount-origin`}
          name="amount_origin"
          value={formData.amount_origin || ""}
          onValueChange={(value) => handleAmountChange("amount_origin", value)}
          suffix={formData.origin_currency}
          placeholder="0.00"
          groupThousands
          aria-invalid={Boolean(formErrors.amount_origin)}
        />
      </Field>

      <div className="flex items-center">
        <div className="h-px flex-1 bg-border" />
        <ArrowDownUp className="mx-4 h-5 w-5 text-muted-foreground" aria-hidden="true" />
        <div className="h-px flex-1 bg-border" />
      </div>

      <Field
        id={`${fieldId}-destination`}
        label={t("destination_label")}
        error={formErrors.destination_currency}
        hint={
          formData.destination_currency &&
          (destinationWallet ? (
            <p className="text-xs text-muted-foreground">
              {t("available_balance_label")}{" "}
              <ColocaAmountDisplay
                value={destinationWallet.balance_available}
                currency={destinationWallet.currency}
                size="sm"
                minimumFractionDigits={2}
                maximumFractionDigits={8}
              />
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">{t("wallet_will_be_activated")}</p>
          ))
        }
      >
        <ColocaSelect
          id={`${fieldId}-destination`}
          value={formData.destination_currency || ""}
          onValueChange={(value) => handleCurrencyChange("destination_currency", value)}
          options={destinationSelectOptions}
          placeholder={t("select_destination")}
        />
      </Field>

      <Field
        id={`${fieldId}-amount-destination`}
        label={t("amount_destination_label")}
        error={formErrors.amount_destination}
        busy={quoteLoading && lastChangedField === "origin"}
      >
        <ColocaAmountInputControlled
          id={`${fieldId}-amount-destination`}
          name="amount_destination"
          value={formData.amount_destination || ""}
          onValueChange={(value) => handleAmountChange("amount_destination", value)}
          suffix={formData.destination_currency}
          placeholder="0.00"
          groupThousands
          aria-invalid={Boolean(formErrors.amount_destination)}
        />
      </Field>

      {destinationInMaintenance && (
        <RailMaintenanceNotice kind="convert" currency={formData.destination_currency} />
      )}

      {quoteLoading && <p className="text-center text-sm text-muted-foreground">{t("loading_quote")}</p>}

      {quoteError && <ColocaAlert variant="destructive" icon={AlertCircle} text={quoteError} />}

      {/* The money trail is the shared component, so a swap and a dispersion read identically. */}
      {quoteData && !quoteLoading && (
        <QuoteBreakdown
          quote={quoteData}
          labels={{
            send: t("you_send"),
            conversionFee: t("conversion_fee"),
            rate: t("conversion_rate"),
            converted: t("converted"),
            serviceFee: t("fees"),
            totalDebit: t("total_debit"),
            receives: t("you_receive"),
          }}
          header={
            <div className="border-b border-border px-5 py-3">
              <QuoteCountdownTimer
                expiresAt={quoteData.expires_at}
                onExpire={markQuoteExpired}
                labels={{
                  validFor: t("quote_valid_for"),
                  expiringSoon: t("quote_expiring_soon"),
                  expired: t("quote_expired_message"),
                }}
              />
            </div>
          }
          footer={
            quoteExpired && (
              <div className="border-t border-border px-5 py-3">
                <ColocaButton
                  variant="secondary"
                  icon={RefreshCw}
                  onClick={handleRecalculate}
                  className="w-full"
                >
                  {t("recalculate_quote")}
                </ColocaButton>
              </div>
            )
          }
        />
      )}

      {submitError && <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />}

      <ColocaButton
        variant="primary"
        onClick={handleSubmit}
        disabled={!isFormValid || isSubmitting}
        loading={isSubmitting}
        className="w-full"
      >
        {t("confirm_swap")}
      </ColocaButton>
    </ColocaSurface>
  );
};
